import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button } from '@monolith/components/0-atoms/Button';
import Icon from '@monolith/components/0-atoms/Icon';
import { STAFF_IMPORT_PAGE_COPY } from '../constants/staffImport';
import { ensureHttpsWebCrawlUrl } from '../helpers/staffImportWebCrawlUrl';
import { staffImportTokens } from '../styles/staffImportTokens';
import { staffImportInProgressButtonStyles } from '../styles/staffImportFieldStyles';
import { StaffImportWarningModal } from './StaffImportWarningModal';

export interface StaffImportWebCrawlFieldProps {
    id: string;
    disabled?: boolean;
    loading?: boolean;
    csvUploadLoading?: boolean;
    hasExistingImport: boolean;
    staffPageUrl?: string | null;
    errorMessage?: string;
    onWebCrawl: (url: string) => void;
    onUrlChange?: () => void;
}

const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${staffImportTokens.spacing.fieldGap}px;
    width: 100%;
`;

const Label = styled.label`
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.label.fontSize};
    font-weight: ${staffImportTokens.typography.label.fontWeight};
    line-height: ${staffImportTokens.typography.label.lineHeight};
    color: ${staffImportTokens.colors.textDefault};
`;

const IconSlot = styled.div<{ $hasError?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex-shrink: 0;
    color: #616161;
    background: ${staffImportTokens.colors.iconSlotBg};
    border: 1px solid
        ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.iconSlotBorder};
    border-right: none;
    border-radius: 12px 0 0 12px;
`;

const UrlInput = styled.input<{ $hasError?: boolean }>`
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 12px;
    border: 1px solid
        ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.grey3};
    border-right: none;
    border-radius: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.label.fontSize};
    font-weight: ${staffImportTokens.typography.label.fontWeight};
    line-height: ${staffImportTokens.typography.label.lineHeight};
    color: ${staffImportTokens.colors.textPrimary};
    background: #ffffff;
    box-sizing: border-box;

    &::placeholder {
        color: ${staffImportTokens.colors.placeholder};
    }

    &:disabled {
        background: #ffffff;
        cursor: not-allowed;
    }
`;

const InputGroup = styled.div<{ $hasError?: boolean }>`
    display: flex;
    align-items: stretch;
    width: 100%;

    &:focus-within ${IconSlot} {
        border-color: ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.primary};
    }

    &:focus-within ${UrlInput} {
        border-color: ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.primary};
    }
`;

const ScanButton = styled(Button)<{
    $inProgress?: boolean;
    $hasError?: boolean;
}>`
    height: 40px;
    min-width: auto;
    gap: 8px;
    border-radius: 0 8px 8px 0;
    border: 1px solid
        ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.primary};
    border-left: none;
    white-space: nowrap;

    ${({ $inProgress }) =>
        $inProgress ? staffImportInProgressButtonStyles : ''}
`;

const HelperText = styled.p`
    margin: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: normal;
    color: #888888;
`;

const ErrorMessage = styled.p`
    margin: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    line-height: 17px;
    color: ${staffImportTokens.colors.red9};
`;

export const StaffImportWebCrawlField: React.FC<
    StaffImportWebCrawlFieldProps
> = ({
    id,
    disabled = false,
    loading = false,
    csvUploadLoading = false,
    hasExistingImport,
    staffPageUrl,
    errorMessage,
    onWebCrawl,
    onUrlChange,
}) => {
    const [webCrawlUrl, setWebCrawlUrl] = useState('');
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [pendingWebCrawlUrl, setPendingWebCrawlUrl] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (csvUploadLoading) {
            setWebCrawlUrl('');
            setPendingWebCrawlUrl(null);
        }
    }, [csvUploadLoading]);

    useEffect(() => {
        if (staffPageUrl) {
            setWebCrawlUrl((current) => current || staffPageUrl);
        }
    }, [staffPageUrl]);

    const trimmedWebCrawlUrl = ensureHttpsWebCrawlUrl(webCrawlUrl);
    const canScan = trimmedWebCrawlUrl.length > 0 && !disabled;
    const resolvedError = errorMessage || undefined;

    const startWebCrawl = (url: string) => {
        onWebCrawl(url);
    };

    const handleScanClick = () => {
        if (!canScan) {
            return;
        }

        if (hasExistingImport) {
            setPendingWebCrawlUrl(trimmedWebCrawlUrl);
            setShowReplaceModal(true);
            return;
        }

        startWebCrawl(trimmedWebCrawlUrl);
    };

    const handleReplaceCancel = () => {
        setShowReplaceModal(false);
        setPendingWebCrawlUrl(null);
    };

    const handleReplaceConfirm = () => {
        if (pendingWebCrawlUrl) {
            startWebCrawl(pendingWebCrawlUrl);
            setPendingWebCrawlUrl(null);
        }
        setShowReplaceModal(false);
    };

    return (
        <FieldContainer>
            <Label htmlFor={id}>{STAFF_IMPORT_PAGE_COPY.webCrawlLabel}</Label>
            <InputGroup $hasError={Boolean(resolvedError)}>
                <IconSlot $hasError={Boolean(resolvedError)} aria-hidden="true">
                    <Icon fasIcon="globe" />
                </IconSlot>
                <UrlInput
                    id={id}
                    type="url"
                    inputMode="url"
                    placeholder={STAFF_IMPORT_PAGE_COPY.webCrawlPlaceholder}
                    value={webCrawlUrl}
                    disabled={disabled}
                    $hasError={Boolean(resolvedError)}
                    aria-invalid={Boolean(resolvedError)}
                    aria-describedby={resolvedError ? `${id}-error` : undefined}
                    onChange={(event) => {
                        setWebCrawlUrl(event.target.value);
                        onUrlChange?.();
                    }}
                />
                <ScanButton
                    id={`${id}-submit`}
                    type="button"
                    variant="primary"
                    size="small"
                    $hasError={Boolean(resolvedError)}
                    $inProgress={loading}
                    loading={loading}
                    onClick={handleScanClick}
                    disabled={!canScan}
                >
                    <Icon farIcon="file-magnifying-glass" />
                    {STAFF_IMPORT_PAGE_COPY.webCrawlButtonLabel}
                </ScanButton>
            </InputGroup>
            {resolvedError ? (
                <ErrorMessage id={`${id}-error`} role="alert">
                    {resolvedError}
                </ErrorMessage>
            ) : null}
            <HelperText>{STAFF_IMPORT_PAGE_COPY.webCrawlHelperText}</HelperText>
            <StaffImportWarningModal
                isOpen={showReplaceModal}
                modalName="staff-import-replace-web-crawl"
                title={STAFF_IMPORT_PAGE_COPY.replaceFileWarningTitle}
                message={STAFF_IMPORT_PAGE_COPY.replaceFileWarningMessage}
                dismissLabel={STAFF_IMPORT_PAGE_COPY.cancelLabel}
                confirmLabel={
                    STAFF_IMPORT_PAGE_COPY.replaceWebCrawlWarningConfirmLabel
                }
                dismissButtonId="staff-import-replace-web-crawl-cancel"
                confirmButtonId="staff-import-replace-web-crawl-confirm"
                onDismiss={handleReplaceCancel}
                onConfirm={handleReplaceConfirm}
            />
        </FieldContainer>
    );
};

export default StaffImportWebCrawlField;
